// src/screens/HotelRoomTypes.jsx
import useIdleLogout from "../hooks/useIdleLogout";
import "../css/HotelRoomTypes.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";

export default function HotelRoomTypes() {
  useIdleLogout();

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [fadeOut, setFadeOut] = useState(false);

  // form state
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [capacity, setCapacity] = useState(2);
  const [status, setStatus] = useState("active");
  const [description, setDescription] = useState("");

  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (message) {
      setFadeOut(false);
      const t1 = setTimeout(() => setFadeOut(true), 1500);
      const t2 = setTimeout(() => setMessage(null), 2000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [message]);

  const resetForm = () => {
    setName("");
    setSlug("");
    setCapacity(2);
    setStatus("active");
    setDescription("");
    setEditingId(null);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setName(row.name || "");
    setSlug(row.slug || "");
    setCapacity(Number(row.capacity || 1));
    setStatus(row.status || "active");
    setDescription(row.description || "");
  };

  const handleDelete = async (row) => {
    const { isConfirmed } = await Swal.fire({
      title: "Are you sure?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });
    if (!isConfirmed) return;

    setRows((prev) => prev.filter((r) => r.id !== row.id));
    Swal.fire("Deleted!", "Room type removed.", "success");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !slug.trim()) {
      Swal.fire("Required", "Room type name and slug are required.", "error");
      return;
    }

    const cap = Number(capacity || 1);
    if (!Number.isFinite(cap) || cap < 1) {
      Swal.fire("Invalid", "Capacity must be at least 1.", "error");
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === editingId
              ? {
                ...r,
                name: name.trim(),
                slug: slug.trim(),
                capacity: cap,
                status,
                description: description.trim(),
                updatedAt: new Date().toISOString(),
              }
              : r
          )
        );
        setMessage({ type: "success", text: "Room type updated successfully!" });
      } else {
        setRows((prev) => [
          ...prev,
          {
            id: Date.now(),
            name: name.trim(),
            slug: slug.trim(),
            capacity: cap,
            status,
            description: description.trim(),
            updatedAt: new Date().toISOString(),
          },
        ]);
        setMessage({ type: "success", text: "Room type added successfully!" });
      }

      resetForm();
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Something went wrong." });
    } finally {
      setLoading(false);
    }
  };

  const formatUpdatedAt = (ts) => {
    if (!ts) return "—";
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  return (
    <div className="container-fluid px-4 hotel-room-types-page">
      <section className="mb-5">
        {/* ================= FORM CARD ================= */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">{editingId ? "Edit Room Type" : "Add Room Type"}</h5>
          </div>

          <div className="card-body">
            {message && (
              <div className={`alert alert-${message.type} ${fadeOut ? "fade-out" : ""}`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Room Type Name:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Standard, Deluxe, Suite"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Code:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. standard, deluxe, suite"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Default Capacity:</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  placeholder="e.g. 2"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Status:</label>
                <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">Description (optional):</label>
                <textarea
                  className="form-control"
                  rows="2"
                  placeholder="Short description about this room type..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* ✅ ONLY 1 FULL-WIDTH BUTTON */}
              <div className="col-12">
                <button type="submit" className="add-type-btn" disabled={loading}>
                  {loading ? "Saving…" : editingId ? "Update Room Type" : "Save Room Type"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ================= TABLE CARD ================= */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Room Types List</h5>
          </div>

          <div className="card-body table-responsive hotel-room-types-table-card">
            {rows.length === 0 ? (
              <p className="text-muted mb-0 p-3">No room types configured yet.</p>
            ) : (
              <table className="table table-bordered align-middle">
                <colgroup>
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "220px" }} />
                  <col style={{ width: "200px" }} />
                  <col style={{ width: "140px" }} />
                  <col />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "220px" }} />
                  <col style={{ width: "160px" }} />
                </colgroup>

                <thead className="table-light">
                  <tr>
                    <th>SI.No</th>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Capacity</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id}>
                      <td>{idx + 1}</td>
                      <td>{row.name}</td>
                      <td className="mono">{row.slug}</td>
                      <td className="mono">{row.capacity}</td>
                      <td>{row.description || <span className="text-muted">—</span>}</td>
                      <td>
                        <span className={`status-text ${row.status === "active" ? "ok" : "bad"}`}>{row.status}</span>
                      </td>
                      <td className="mono">{formatUpdatedAt(row.updatedAt)}</td>
                      <td className="actions-cell justify-content-end">
                        <button type="button" className="btn-view" title="Edit" onClick={() => startEdit(row)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button type="button" className="btn-delete" title="Delete" onClick={() => handleDelete(row)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
